import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { INTL_LOCALE, type Locale } from "~/i18n";
import { isSameDay } from "~/lib/calendar";
import type { EventSummary } from "~/types/event";

/**
 * When and where an event happens.
 *
 * Times are stored as UTC instants and rendered here in the member's own timezone —
 * `Intl` does that without being told which zone that is. An end time on the same day
 * is shown as a range (`18:00 – 19:30`); one that crosses midnight repeats the date, so
 * a two-day event never reads as ending before it began.
 */
export function EventTime({
  event,
  withDate = false,
}: {
  event: EventSummary;
  withDate?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const intlLocale = INTL_LOCALE[i18n.language as Locale] ?? INTL_LOCALE.cs;

  const starts = new Date(event.starts_at);
  const ends = event.ends_at ? new Date(event.ends_at) : null;

  const time = new Intl.DateTimeFormat(intlLocale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateAndTime = new Intl.DateTimeFormat(intlLocale, {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

  const formatter = withDate ? dateAndTime : time;
  let label = formatter.format(starts);
  if (ends) {
    // A range that crosses midnight has to repeat the date to stay unambiguous.
    label += ` – ${(isSameDay(starts, ends) ? time : dateAndTime).format(ends)}`;
  }

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}
    >
      <Typography variant="L2" sx={{ color: "text.secondary" }}>
        <time dateTime={event.starts_at}>{label}</time>
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "text.muted",
        }}
      >
        {event.location ? (
          <PlaceOutlinedIcon sx={{ fontSize: "1rem" }} />
        ) : (
          <VideocamOutlinedIcon sx={{ fontSize: "1rem" }} />
        )}
        <Typography variant="L2" sx={{ color: "text.muted" }}>
          {event.location ?? t("events.online")}
        </Typography>
      </Box>
    </Box>
  );
}
